<template>
  <div class="login-container">
    <!-- 使用新的广告轮播组件 -->
    <AdCarousel 
      v-if="showAdCarousel"
      :auto-play="true"
      :interval="3000"
      :show-close-button="true"
      :show-ad-info="false"
      :enable-keyboard-nav="true"
      @close="handleAdClose"
      @error="handleAdError"
    />
    <!-- 整体登录框，左右布局 -->
    <div class="login-box">
      <!-- 左侧图片区域 -->
      <div class="login-left">
        <img src="../assets/background.jpg" alt="登陆背景" />
      </div>
      <!-- 右侧表单区域 -->
      <div class="login-right">
        <h3>豪赋医学订单系统</h3>
        <h5>用户登录</h5>
        <el-form :model="loginForm" :rules="rules" ref="loginFormRef">
          <el-form-item prop="username">
            <el-input
                v-model="loginForm.username"
                placeholder="用户名"
                class="custom-input"
            />
          </el-form-item>
          <el-form-item prop="password">
            <el-input
                v-model="loginForm.password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="密码"
                autocomplete="off"
                class="custom-input"
            >
              <template #suffix>
                <el-button
                    @click="showPassword = !showPassword"
                    link
                    type="primary"
                    style="padding:0 6px;"
                    tabindex="-1"
                >
                  <el-icon>
                    <svg
                        viewBox="0 0 1024 1024"
                        width="18"
                        height="18"
                        :style="{ fill: showPassword ? '#409EFF' : '#bfbfbf' }"
                    >
                      <path d="M512 224c-198.4 0-362.7 128-448 288 85.3 160 249.6 288 448 288s362.7-128 448-288c-85.3-160-249.6-288-448-288z m0 480c-106 0-192-86-192-192s86-192 192-192 192 86 192 192-86 192-192 192z m0-320c-70.7 0-128 57.3-128 128s57.3 128 128 128 128-57.3 128-128-57.3-128-128-128z"/>
                    </svg>
                  </el-icon>
                </el-button>
              </template>
            </el-input>
          </el-form-item>
          <el-form-item>
            <el-checkbox v-model="rememberMe">记住账号</el-checkbox>
          </el-form-item>
          <el-form-item>
            <el-button
                type="primary"
                @click="handleLogin"
                class="login-btn"
                :loading="isLoggingIn"
                :disabled="isLoggingIn"
            >
              登录
            </el-button>
          </el-form-item>
          <!-- 新增：主推产品文案 -->
          <div class="product-info">
            主推产品：超主点®、优薄 - X®
          </div>
        </el-form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import axios from 'axios'
import AdCarousel from '@/components/AdCarousel.vue'
import { AD_CONFIG } from '@/composables/useAdService'

// 控制密码显示/隐藏
const showPassword = ref(false)
// 登录按钮加载/节流状态
const isLoggingIn = ref(false)

// 广告相关状态
const showAdCarousel = ref(true)


const router = useRouter()
const loginFormRef = ref(null)

const loginForm = reactive({
  username: '',
  password: ''
})

// 记住账号复选框
const rememberMe = ref(false)

// 广告事件处理方法
const handleAdClose = () => {
  showAdCarousel.value = false
}



const handleAdError = (error) => {
  console.error('广告加载出错:', error)
  showAdCarousel.value = false
  // 可以显示一个友好的错误提示
}


// 页面挂载时，检查本地是否存有账号密码和广告显示状态
onMounted(() => {
  // 处理记住我逻辑
  const savedRemember = localStorage.getItem('rememberMe')
  if (savedRemember === 'true') {
    const savedUsername = localStorage.getItem('savedUsername')
    const savedPassword = localStorage.getItem('savedPassword')
    if (savedUsername) loginForm.username = savedUsername
    if (savedPassword) loginForm.password = savedPassword
    rememberMe.value = true
  }

  // 检查用户今日是否已关闭广告
  const today = new Date().toDateString()
  const closedToday = localStorage.getItem(AD_CONFIG.STORAGE_KEYS.AD_CLOSED)
  if (closedToday === today) {
    showAdCarousel.value = false
  }
})

const rules = {
  username: [
    { required: true, message: '请输入用户名', trigger: 'blur' }
  ],
  password: [
    { required: true, message: '请输入密码', trigger: 'blur' }
  ]
}

const handleLogin = async () => {
  if (isLoggingIn.value) return; // 防止连点

  if (!loginForm.username || !loginForm.password) {
    ElMessage.error('请输入用户名和密码');
    return;
  }

  isLoggingIn.value = true;

  try {
    // 清除之前的登录状态
    localStorage.clear();
    axios.defaults.headers.common['token'] = '';

    const loginUrl = '/api/v1/user/login';

    const response = await axios.post(`${loginUrl}?username=${encodeURIComponent(loginForm.username)}&password=${encodeURIComponent(loginForm.password)}`);


    if (response.data.code === '200') {
      const userInfo = response.data.data;

      // 存储用户信息
      localStorage.setItem('userInfo', JSON.stringify(userInfo));
      localStorage.setItem('userId', userInfo.userId);

      // 从响应头获取 token
      const token = response.headers.authorization;
      if (token) {
        localStorage.setItem('token', token);
        // 设置默认的 token header
        axios.defaults.headers.common['Authorization'] = token;
      }

      // 根据用户角色自动跳转到对应页面
      let redirectPath;
      if (userInfo.role === 'ADMIN') {
        redirectPath = '/admin/orders';
        ElMessage.success('管理员登录成功');
      } else {
        redirectPath = '/user/orders';
        ElMessage.success('用户登录成功');
      }

      // 使用 router.push 进行跳转
      await router.push(redirectPath);

      // ===== 记住账号逻辑 =====
      if (rememberMe.value) {
        localStorage.setItem('savedUsername', loginForm.username);
        localStorage.setItem('savedPassword', loginForm.password);
        localStorage.setItem('rememberMe', 'true');
      } else {
        localStorage.removeItem('savedUsername');
        localStorage.removeItem('savedPassword');
        localStorage.setItem('rememberMe', 'false');
      }
      // =========================
    } else {
      ElMessage.error(response.data.info || '登录失败');
    }
  } catch (error) {
    console.log('登录失败:', error);
    console.error('登录失败:', error);
    if (error.response && error.response.data) {
      ElMessage.error(error.response.data.info || '登录失败，请检查用户名和密码');
    } else {
      ElMessage.error('登录失败，请检查用户名和密码');
    }
  } finally {
    isLoggingIn.value = false;
  }
};

</script>

<style scoped>
.login-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 95vh;
  background-color: #469BC6;
  border-radius: 8px;
  box-sizing: border-box;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.4);
}

/* 登录框禁用状态 */
.login-box.disabled {
  pointer-events: none;
  filter: blur(2px);
  opacity: 0.5;
}

/* 整体登录框：左右分割，白色背景 + 蓝色边框 */
.login-box {
  display: flex;
  width: 800px;
  background: #fff;
  border-radius: 8px;
  overflow: hidden; /* 让圆角生效 */
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
}

.login-left {
  width: 50%;
}
.login-left img {
  width: 100%;
  height: 100%;
  object-fit: cover; /* 保持图片比例，铺满容器 */
}

/* 右侧表单区域：占比 50%，padding 撑出间距 */
.login-right {
  width: 50%;
  padding: 40px 50px;
  box-sizing: border-box;
}

/* 标题样式：调整颜色、间距 */
.login-right h3 {
  text-align: center;
  margin-bottom: 20px;
  color: #469BC6;
  font-size: 20px;
  font-weight: 600;
}
.login-right h5 {
  text-align: center;
  margin-bottom: 30px;
  color: #469BC6;
  font-size: 16px;
  font-weight: 400;
}
:deep(.el-checkbox-checked-text-color){
  color: #58595B;
}

/* 输入框样式：下划线 + 蓝色光标 */
.custom-input :deep(.el-input__wrapper) {
  border: none !important;
  border-radius: 0 !important;
  border-bottom: 1px solid #469BC6 !important;
  box-shadow: none !important;
  background-color: transparent !important;
  padding: 8px 0 4px 0 !important;
  transition: border-bottom-color 0.3s ease;
}

/* 聚焦状态 */
.custom-input :deep(.el-input__wrapper.is-focus) {
  border-bottom: 2px solid #007acc !important;
  box-shadow: none !important;
}

/* 悬停状态 */
.custom-input :deep(.el-input__wrapper:hover) {
  border-bottom: 1px solid #007acc !important;
  box-shadow: none !important;
}

/* 输入框内部文本 */
.custom-input :deep(.el-input__inner) {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  outline: none !important;
  padding: 0 !important;
}

.custom-input :deep(.el-input__suffix) {
  background: transparent !important;
}

/* 登录按钮 */
.login-btn {
  width: 100%;
  background-color: #469BC6;
  border: none;
  height: 44px;
  font-size: 16px;
}

.login-btn:hover {
  background-color: #007acc;
}


/* 主推产品文案 */
.product-info {
  text-align: center;
  margin-top: 20px;
  color: #469BC6;
  font-size: 14px;
}

/* 平板端适配 (768px - 1024px) */
@media (max-width: 1024px) {
  .login-box {
    max-width: 700px;
  }

  .login-right {
    padding: 30px 40px;
  }

  .login-right h3 {
    font-size: 18px;
  }

  .login-right h5 {
    font-size: 15px;
  }
}

/* 移动端适配 (≤768px) */
@media (max-width: 768px) {
  .login-container {
    align-items: center;
    padding: 15px;
  }

  .login-box {
    flex-direction: column;
    width: 100%;
  }

  .login-left {
    width: 100%;
    height: 200px;
    order: 1;
  }

  .login-right {
    width: 100%;
    padding: 30px 25px;
    order: 2;
  }

  .login-right h3 {
    font-size: 18px;
    margin-bottom: 15px;
  }

  .login-right h5 {
    font-size: 14px;
    margin-bottom: 25px;
  }

  .login-btn {
    height: 46px;
    font-size: 16px;
  }

  .product-info {
    font-size: 13px;
    margin-top: 15px;
  }
}

/* 小屏手机适配 (≤480px) */
@media (max-width: 480px) {
  .login-container {
    padding: 10px;
    padding-top: 3vh;
  }

  .login-box {
    max-width: 100%;
  }

  .login-left {
    height: 160px;
  }

  .login-right {
    padding: 25px 20px;
  }

  .login-right h3 {
    font-size: 16px;
    margin-bottom: 12px;
  }

  .login-right h5 {
    font-size: 13px;
    margin-bottom: 20px;
  }

  .login-btn {
    height: 44px;
    font-size: 15px;
  }

  .product-info {
    font-size: 12px;
    margin-top: 12px;
  }

  /* 调整表单项间距 */
  :deep(.el-form-item) {
    margin-bottom: 18px;
  }

  /* 调整复选框文字大小 */
  :deep(.el-checkbox__label) {
    font-size: 14px;
  }

}

/* 超小屏适配 (≤375px) */
@media (max-width: 375px) {
  .login-right {
    padding: 20px 15px;
  }

  .login-right h3 {
    font-size: 15px;
  }

  .login-right h5 {
    font-size: 12px;
  }

  .login-btn {
    height: 42px;
    font-size: 14px;
  }

  .product-info {
    font-size: 11px;
    line-height: 1.4;
  }
}

/* 横屏适配 */
@media (max-height: 600px) and (orientation: landscape) {
  .login-container {
    padding-top: 2vh;
  }


  .login-right {
    padding: 20px 25px;
  }

  .login-right h3 {
    margin-bottom: 10px;
  }

  .login-right h5 {
    margin-bottom: 15px;
  }

  :deep(.el-form-item) {
    margin-bottom: 15px;
  }

  .product-info {
    margin-top: 10px;
  }
}

/* 触摸优化 */
@media (hover: none) and (pointer: coarse) {
  .login-btn {
    height: 48px; /* 增加触摸区域 */
  }


  /* 输入框触摸优化 */
  .custom-input :deep(.el-input__inner) {
    font-size: 16px; /* 防止iOS缩放 */
  }
}
</style>